import {
  DESTINATION_ORDER,
  DESTINATIONS,
  contentTypeForDestination,
  destinationById,
  destinationOfTarget,
  type PostDestination,
  type PostDestinationId,
} from '../../lib/publishing/destinations';
import type {
  FbContentType,
  GbpConfig,
  IgContentType,
  Post,
  PostInput,
  PostMedia,
  PostTargetInput,
  PublishMode,
} from '../../types/publishing';

// Publishing composer — the client-side draft state model + pure transforms
// (state ⇄ PostInput ⇄ loaded Post). Kept pure + separate from the React
// component so the mapping is unit-testable.
//
// KEYED BY DESTINATION, NOT PLATFORM (composer redesign, 2026-07-20). It used to
// be Record<PostPlatform, …>, which structurally cannot express "Facebook feed
// AND Facebook Story in one post" — the exact thing this batch adds. Each of the
// five destinations now carries its own overrides and its own optional time, and
// emits its own entry in targets[].

// Per-destination config, always present for all five (enabled toggles it).
export interface ComposerTarget {
  destination: PostDestinationId;
  enabled: boolean;
  // The three tick-boxes in the destination's options block. Kept SEPARATE from
  // the values so unticking "Caption override" doesn't destroy what was typed —
  // the AM can toggle it back and the text is still there. Only ticked overrides
  // are emitted.
  overrideCaption: boolean;
  customContent: string;
  overrideMedia: boolean;
  customMedia: PostMedia[];
  addFirstComment: boolean;
  firstComment: string;
  // Feed-surface sub-choice (feed vs Reel). Ignored on story destinations, whose
  // content type is fixed by the destination itself.
  igContentType: IgContentType;
  fbContentType: FbContentType;
  gbp: GbpConfig;
  // Per-destination schedule override; null = use the post-level time.
  scheduledFor: string | null;
}

export interface ComposerState {
  kyckAccountId: string;
  content: string;
  media: PostMedia[];
  targets: Record<PostDestinationId, ComposerTarget>;
  publishMode: PublishMode;
  scheduledFor: string | null; // the post-level "apply to all" time
  queueId: string | null;
}

// Re-exported so callers don't need two imports to iterate destinations.
export { DESTINATION_ORDER, DESTINATIONS };

export function defaultTarget(destination: PostDestinationId): ComposerTarget {
  return {
    destination,
    enabled: false,
    overrideCaption: false,
    customContent: '',
    overrideMedia: false,
    customMedia: [],
    addFirstComment: false,
    firstComment: '',
    igContentType: 'feed',
    fbContentType: 'feed',
    gbp: { topicType: 'STANDARD', cta: null, event: null, offer: null },
    scheduledFor: null,
  };
}

function emptyTargets(): Record<PostDestinationId, ComposerTarget> {
  return Object.fromEntries(DESTINATION_ORDER.map((id) => [id, defaultTarget(id)])) as Record<
    PostDestinationId,
    ComposerTarget
  >;
}

export function emptyComposerState(kyckAccountId = ''): ComposerState {
  return {
    kyckAccountId,
    content: '',
    media: [],
    targets: emptyTargets(),
    publishMode: 'draft',
    scheduledFor: null,
    queueId: null,
  };
}

export function enabledTargets(state: ComposerState): ComposerTarget[] {
  return DESTINATION_ORDER.map((id) => state.targets[id]).filter((t) => t.enabled);
}

// ---------------------------------------------------------------------------
// Combined Story options (item 2, 2026-07-28)
// ---------------------------------------------------------------------------
//
// FB Story and IG Story always share the same asset — you never want different
// media per story surface — so the composer consolidates their OPTIONS block into
// one when both are ticked. The two destination TICKS and the two underlying
// targets are unchanged; this is purely a UI consolidation, so the AM sets the
// story asset once. These pure helpers make the grouping + lockstep-write
// unit-testable, separate from the React wiring.

/** Both story destinations ticked → render the ONE combined Story block. */
export function bothStoriesEnabled(
  targets: Record<PostDestinationId, ComposerTarget>,
): boolean {
  return targets.facebook_story.enabled && targets.instagram_story.enabled;
}

/**
 * The override fields a Story options block edits. The combined block writes
 * these to BOTH story targets; each target keeps its own destination / enabled /
 * schedule (those are never shared).
 */
export function sharedStoryFields(
  t: ComposerTarget,
): Pick<
  ComposerTarget,
  'overrideCaption' | 'customContent' | 'overrideMedia' | 'customMedia' | 'addFirstComment' | 'firstComment'
> {
  return {
    overrideCaption: t.overrideCaption,
    customContent: t.customContent,
    overrideMedia: t.overrideMedia,
    customMedia: t.customMedia,
    addFirstComment: t.addFirstComment,
    firstComment: t.firstComment,
  };
}

/** Apply a combined-Story-block edit to BOTH story targets in lockstep. */
export function applyStoryGroupChange(
  targets: Record<PostDestinationId, ComposerTarget>,
  next: ComposerTarget,
): Record<PostDestinationId, ComposerTarget> {
  const shared = sharedStoryFields(next);
  return {
    ...targets,
    facebook_story: { ...targets.facebook_story, ...shared },
    instagram_story: { ...targets.instagram_story, ...shared },
  };
}

export function selectedDestinationIds(state: ComposerState): PostDestinationId[] {
  return enabledTargets(state).map((t) => t.destination);
}

/**
 * A destination's EFFECTIVE caption — its override when ticked and non-empty,
 * else the base. The character counters and the previews both read this, so what
 * the counter measures is always what that destination will publish.
 */
export function effectiveCaption(state: ComposerState, id: PostDestinationId): string {
  const t = state.targets[id];
  if (t && t.overrideCaption && t.customContent.trim() !== '') return t.customContent;
  return state.content;
}

/** A destination's effective media — its override when ticked and non-empty. */
export function effectiveMedia(state: ComposerState, id: PostDestinationId): PostMedia[] {
  const t = state.targets[id];
  if (t && t.overrideMedia && t.customMedia.length > 0) return t.customMedia;
  return state.media;
}

// The per-destination config as a PostTargetInput. Only TICKED overrides are
// emitted, so an override the AM disabled doesn't quietly keep applying.
function toTargetInput(t: ComposerTarget, publishMode: PublishMode): PostTargetInput {
  const destination = destinationById(t.destination);
  const input: PostTargetInput = { platform: destination.platform };

  const ct = contentTypeForDestination(destination, { fb: t.fbContentType, ig: t.igContentType });
  if (ct.fbContentType) input.fbContentType = ct.fbContentType;
  if (ct.igContentType) input.igContentType = ct.igContentType;

  if (t.overrideCaption && t.customContent.trim() !== '') input.customContent = t.customContent;
  if (t.overrideMedia && t.customMedia.length > 0) input.customMedia = t.customMedia;
  if (destination.supportsFirstComment && t.addFirstComment && t.firstComment.trim() !== '') {
    input.firstComment = t.firstComment;
  }
  if (destination.platform === 'googlebusiness') input.gbp = t.gbp;
  // A per-destination time only means anything on a scheduled post.
  if (publishMode === 'schedule' && t.scheduledFor) input.scheduledFor = t.scheduledFor;
  return input;
}

export function toPostInput(state: ComposerState): PostInput {
  return {
    kyckAccountId: state.kyckAccountId,
    content: state.content,
    media: state.media,
    targets: enabledTargets(state).map((t) => toTargetInput(t, state.publishMode)),
    scheduledFor: state.publishMode === 'schedule' ? state.scheduledFor : null,
    publishMode: state.publishMode,
    queueId: state.publishMode === 'queue' ? state.queueId : null,
  };
}

// Hydrate composer state from a loaded post (edit flow). Each stored target
// resolves to its destination via its content type, so a legacy post (whose
// targets predate placements) hydrates onto the feed destinations unchanged.
// What hydration actually reads. A stored `Post` satisfies it as-is; another host
// (the club portal, whose post projection is narrower than the stored doc) adapts
// into it rather than growing a parallel state model.
export interface ComposerSource {
  kyckAccountId: string;
  content: string;
  media: PostMedia[];
  publishMode: PublishMode;
  scheduledFor: string | null;
  queueId: string | null;
  targets: ReadonlyArray<{
    platform: PostTargetInput['platform'];
    igContentType?: IgContentType | null;
    fbContentType?: FbContentType | null;
    gbp?: GbpConfig | null;
    customContent?: string | null;
    customMedia?: PostMedia[] | null;
    firstComment?: string | null;
    scheduledFor?: string | null;
  }>;
}

export function fromPost(post: ComposerSource): ComposerState {
  const state = emptyComposerState(post.kyckAccountId);
  state.content = post.content;
  state.media = post.media;
  state.publishMode = post.publishMode;
  state.scheduledFor = post.scheduledFor;
  state.queueId = post.queueId;
  for (const target of post.targets) {
    const destination: PostDestination = destinationOfTarget(target);
    const t = state.targets[destination.id];
    if (!t) continue;
    t.enabled = true;
    // The tick-boxes are re-derived from what was actually stored: a stored
    // override means it was ticked.
    t.customContent = target.customContent ?? '';
    t.overrideCaption = t.customContent.trim() !== '';
    t.customMedia = target.customMedia ?? [];
    t.overrideMedia = t.customMedia.length > 0;
    t.firstComment = target.firstComment ?? '';
    t.addFirstComment = t.firstComment.trim() !== '';
    // Only the FEED sub-choice is worth restoring; a story destination's content
    // type is implied by the destination.
    if (target.igContentType && target.igContentType !== 'story') {
      t.igContentType = target.igContentType;
    }
    if (target.fbContentType && target.fbContentType !== 'story') {
      t.fbContentType = target.fbContentType;
    }
    if (target.gbp) t.gbp = target.gbp;
    t.scheduledFor = target.scheduledFor ?? null;
  }
  return state;
}

// Is the post read-only (published)? Published FB/IG/GBP can't be edited.
export function isReadOnlyStatus(status: Post['status']): boolean {
  return status === 'published' || status === 'partial' || status === 'publishing';
}
